import stopImg from "@assets/images/stop.png";
import type { PaymentProduct } from "@features/payment/paymentResultNavigation";
import { resolvePaymentSuccessContinue } from "@features/payment/paymentSuccessContent";
import { Card } from "@shared/components/Card";
import { Link, useSearchParams } from "react-router-dom";

const DEFAULT_AMOUNT_PENCE = 399;

function formatAmountPence(pence: number): string {
  const pounds = pence / 100;
  return `£${pounds.toFixed(2)}`;
}

function parseProduct(value: string | null): PaymentProduct {
  if (value === "event_access_unlock" || value === "event_vip_unlock") {
    return value;
  }
  return "default";
}

export function PaymentFailedPage() {
  const [searchParams] = useSearchParams();
  const product = parseProduct(searchParams.get("product"));
  const amountPence = Number(searchParams.get("amount")) || DEFAULT_AMOUNT_PENCE;
  const amountFormatted = formatAmountPence(amountPence);
  const errorMessage = searchParams.get("error")?.trim();
  const { path: retryPath } = resolvePaymentSuccessContinue(product, searchParams.get("next"));

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <main className="section-padding flex min-h-[100dvh] flex-col items-center justify-center">
        <div className="mx-auto flex w-full max-w-[var(--content-max-width)] flex-col items-center gap-6">
          <div className="flex h-[200px] w-[200px] items-center justify-center overflow-hidden rounded-full bg-[#FFEBEE]">
            <img src={stopImg} alt="" className="h-[200px] w-[200px] object-cover" />
          </div>

          <h1 className="font-mono text-[30px] font-semibold text-[#FF5963]">Payment Failed</h1>

          <p className="max-w-md text-center font-mono text-[length:var(--font-size-body-large)] font-semibold text-[#606A85]">
            We couldn&apos;t process your payment at this time. Please check your payment details and try again.
          </p>

          <Card constrainWidth className="flex flex-col gap-3 rounded-2xl p-4 text-left">
            <h2 className="font-mono text-[length:var(--font-size-headline-small)] font-semibold text-[#15161E]">
              Payment Details
            </h2>
            <div className="flex justify-between font-mono">
              <span className="text-[length:var(--font-size-body-medium)] font-semibold text-[#606A85]">Amount:</span>
              <span className="text-[length:var(--font-size-body-large)] font-semibold text-[#15161E]">
                {amountFormatted}
              </span>
            </div>
            <div className="flex justify-between gap-4 font-mono">
              <span className="shrink-0 text-[length:var(--font-size-body-medium)] font-semibold text-[#606A85]">
                Error:
              </span>
              <span className="text-right text-[length:var(--font-size-body-small)] font-semibold text-[#FF5963]">
                {errorMessage ?? "Payment could not be completed"}
              </span>
            </div>
          </Card>

          <div className="flex w-full max-w-[var(--content-max-width)] flex-col gap-6">
            <Link
              to={retryPath}
              className="flex h-14 w-full items-center justify-center rounded-button-lg font-mono text-[length:var(--font-size-title-medium)] font-semibold text-white shadow-md transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              style={{ backgroundColor: "#6F61EF" }}
              data-testid="try-again-button"
            >
              Try Again
            </Link>
            <Link
              to="/matches"
              className="flex h-14 w-full items-center justify-center rounded-button-lg border-2 border-[var(--color-border-card)] font-mono text-[length:var(--font-size-title-medium)] font-semibold text-[#606A85] transition-colors hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              data-testid="cancel-button"
            >
              Continue with messages only
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
